import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import type { FeatureFlag } from '@/config/availableFeatureFlags';
import { isNonProduction } from '@/utils/environmentByHostname';
import { getLocalFeatureFlags } from '@/utils/localFeatureFlags';

export const FEATURE_FLAGS_CHANGED_EVENT = 'optoscale-feature-flags-changed';

interface FeatureFlagsContextValue {
    hasFeature: (flagName: FeatureFlag | FeatureFlag[]) => boolean;
    localFlags: Record<string, boolean>;
    isNonProduction: boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
    hasFeature: () => false,
    localFlags: {},
    isNonProduction: false,
});

interface FeatureFlagsProviderProps {
    children: React.ReactNode;
}

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
    const [localFlags, setLocalFlags] = useState<Record<string, boolean>>({});
    const nonProduction = isNonProduction();

    // Initialize local flags
    useEffect(() => {
        if (nonProduction) {
            setLocalFlags(getLocalFeatureFlags());
        }
    }, [nonProduction]);

    // Listen for feature flag changes (when flags are modified from console)
    useEffect(() => {
        if (!nonProduction) return;

        const handleFeatureFlagsChange = (event: CustomEvent) => {
            setLocalFlags(event.detail.flags);
        };

        window.addEventListener(
            FEATURE_FLAGS_CHANGED_EVENT,
            handleFeatureFlagsChange as EventListener
        );

        return () => {
            window.removeEventListener(
                FEATURE_FLAGS_CHANGED_EVENT,
                handleFeatureFlagsChange as EventListener
            );
        };
    }, [nonProduction]);

    const hasFeature = (flagName: FeatureFlag | FeatureFlag[]): boolean => {
        // If not in non-production, always return false for local flags
        if (!nonProduction) {
            return false;
        }

        // Handle single flag or array of flags
        const flags = Array.isArray(flagName) ? flagName : [flagName];

        // Check if any of the requested flags are enabled locally
        return flags.some((flag) => localFlags[flag] === true);
    };

    const value: FeatureFlagsContextValue = {
        hasFeature,
        localFlags,
        isNonProduction: nonProduction,
    };

    return <FeatureFlagsContext value={value}>{children}</FeatureFlagsContext>;
}

export function useFeatureFlagsContext(): FeatureFlagsContextValue {
    const context = useContext(FeatureFlagsContext);
    if (!context) {
        throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
    }
    return context;
}
